import React, { forwardRef } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { ButtonProps } from './types';
import { typeography } from '../Typography';

type LinkAsButtonSize = 'detailLarge' | 'large' | 'small';

export type LinkButtonProps = Pick<
  ButtonProps,
  'disabled' | 'className' | 'label' | 'onClick' | 'type' | 'id' | 'data-heap-id'
> & {
  size?: LinkAsButtonSize;
  truncatable?: boolean;
  /** When true, keeps text-decoration: none on hover (no underline on hover) */
  noUnderlineOnHover?: boolean;
};

const StyleConfigs: Record<LinkAsButtonSize, { lineHeight: number; fontSize: string }> = {
  detailLarge: {
    lineHeight: typeography.lineHeight.condensed,
    fontSize: typeography.fontSize.detailLarge,
  },
  small: {
    lineHeight: typeography.lineHeight.condensed,
    fontSize: typeography.fontSize.bodySmall,
  },
  large: {
    lineHeight: typeography.lineHeight.default,
    fontSize: typeography.fontSize.bodyRegular,
  },
};

const StyledLinkAsButton = styled.button<{
  disabled: boolean;
  $size: LinkAsButtonSize;
  $truncatable?: boolean;
  $noUnderlineOnHover?: boolean;
}>`
  border: 0;
  margin: 0;
  padding: 0;
  background: transparent;
  color: ${({ theme, disabled }) => {
    return disabled ? theme.designSystem.textColors.disabled : theme.designSystem.textColors.link;
  }};
  ${({ $truncatable }) => {
    if (!$truncatable) return '';

    return `
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    `;
  }}
  font-weight: 500;
  text-decoration: none;
  line-height: ${({ $size }): string => `${StyleConfigs[$size].lineHeight}rem`};
  font-size: ${({ $size }): string => StyleConfigs[$size].fontSize};

  @media (any-pointer: fine) {
    &:hover {
      color: ${({ theme, disabled }) => {
        return disabled ? theme.designSystem.textColors.disabled : theme.designSystem.textColors.linkHover;
      }};
      text-decoration: ${({ disabled, $noUnderlineOnHover }) => {
        if (disabled || $noUnderlineOnHover) return 'none';
        return 'underline';
      }};
    }
  }
`;

const validateSize = ({ isSmall, size }: { isSmall?: boolean; size?: LinkAsButtonSize }): LinkAsButtonSize => {
  if (isSmall) {
    return 'small';
  }

  return ['small', 'large', 'detailLarge'].includes(size) ? size : 'large';
};

type DeprecatedProps = {
  /** was here to match Button props, but has been replaced with "size" prop */
  isSmall?: boolean;
};

/**
 * It looks like a link but behaves like a button. AKA it looks like a link, but has an onClick event like a button.
 * [Figma](https://www.figma.com/file/AiDAD1WpprgfbZI2XV6hU1/Casting-Networks-Design-System?type=design&node-id=271-1&mode=dev)
 * See the "Links" section
 *
 * Use this when you are simply navigating from page to view/page but the design commands a button look.
 *
 * This is a customized component that would replace a `<Link as="Button" onClick={() => {}} />` component
 * that would LOOK like a link but have an onClick handler and not a "to" prop.
 *
 * @example
 * <LinkAsButton onClick={() => {}} label="Profile" />
 * <LinkAsButton onClick={() => {}}>Profile</LinkAsButton>
 */

type LinkAsButtonProps = LinkButtonProps & DeprecatedProps & { children?: React.ReactNode };

export const LinkAsButton = forwardRef<HTMLButtonElement, LinkAsButtonProps>(
  (
    {
      label,
      children,
      disabled = false,
      className,
      onClick,
      size = 'large',
      isSmall,
      type = 'button',
      truncatable = false,
      noUnderlineOnHover = false,
      ...rest
    },
    ref
  ) => {
    const normalizedSize = validateSize({ isSmall, size });

    return (
      <StyledLinkAsButton
        type={type}
        {...rest}
        $size={normalizedSize}
        disabled={disabled}
        className={classNames(className, { disabled })}
        onClick={disabled ? () => undefined : onClick}
        $truncatable={truncatable}
        $noUnderlineOnHover={noUnderlineOnHover}
        ref={ref}
      >
        {label || children}
      </StyledLinkAsButton>
    );
  }
);
